/* ====================================================================
   WCT REHAUL — motion layer for the new sections (hero bg, explosion
   scroll-scrub, orchestration loop, bracket cursor, generic reveals).
   Progressive enhancement: everything is visible without this file;
   it only adds motion on top. Dependency-free: the explosion pin is
   pure CSS (position: sticky) and the scrub is direct scroll math, so
   nothing here waits on a ticker or an animation library.
   ==================================================================== */
(function () {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  document.documentElement.classList.add('js-wct-motion');

  /* ---------------------------------------------------------------
     [ 00 ] Hero constellation — code-drawn, not generated. Sparse
     drifting nodes joined by proximity lines on the void; a few
     accent nodes pulse in the brand colours. Pauses offscreen.
     --------------------------------------------------------------- */
  function initConstellation() {
    var canvas = document.querySelector('[data-wct-constellation]');
    if (!canvas) return;
    var ctx = canvas.getContext('2d');
    if (!ctx) return;

    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var w = 0;
    var h = 0;
    var nodes = [];
    var LINK_DIST = 170;

    function build() {
      var rect = canvas.parentElement.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      var count = Math.max(22, Math.min(56, Math.round((w * h) / 42000)));
      nodes = [];
      for (var i = 0; i < count; i++) {
        // accents: a few green, one red signal — the rest quiet cream
        var accent = i === 0 ? '#ff3d2e' : i < 5 ? '#c8e6a0' : null;
        nodes.push({
          x: Math.random() * w,
          y: Math.random() * h,
          vx: (Math.random() - 0.5) * 0.22,
          vy: (Math.random() - 0.5) * 0.22,
          r: accent ? 2.2 : 1 + Math.random() * 1.2,
          accent: accent,
          phase: Math.random() * Math.PI * 2,
        });
      }
    }

    function step(t) {
      ctx.clearRect(0, 0, w, h);
      var i, j, a, b;
      for (i = 0; i < nodes.length; i++) {
        a = nodes[i];
        a.x += a.vx;
        a.y += a.vy;
        if (a.x < -20) a.x = w + 20; else if (a.x > w + 20) a.x = -20;
        if (a.y < -20) a.y = h + 20; else if (a.y > h + 20) a.y = -20;
      }
      ctx.lineWidth = 1;
      for (i = 0; i < nodes.length; i++) {
        a = nodes[i];
        for (j = i + 1; j < nodes.length; j++) {
          b = nodes[j];
          var dx = a.x - b.x;
          var dy = a.y - b.y;
          var d = Math.sqrt(dx * dx + dy * dy);
          if (d < LINK_DIST) {
            ctx.strokeStyle = 'rgba(243,239,231,' + (0.16 * (1 - d / LINK_DIST)).toFixed(3) + ')';
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }
      for (i = 0; i < nodes.length; i++) {
        a = nodes[i];
        var pulse = a.accent ? 0.55 + 0.45 * Math.sin(t / 900 + a.phase) : 1;
        ctx.beginPath();
        ctx.arc(a.x, a.y, a.r, 0, Math.PI * 2);
        if (a.accent) {
          ctx.shadowColor = a.accent;
          ctx.shadowBlur = 10 * pulse;
          ctx.fillStyle = a.accent;
          ctx.globalAlpha = pulse;
        } else {
          ctx.shadowBlur = 0;
          ctx.fillStyle = 'rgba(243,239,231,0.5)';
          ctx.globalAlpha = 1;
        }
        ctx.fill();
        ctx.globalAlpha = 1;
        ctx.shadowBlur = 0;
      }
    }

    var running = false;
    var rafId = null;
    function loop(t) {
      if (!running) return;
      step(t);
      rafId = requestAnimationFrame(loop);
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting && !running) {
          running = true;
          rafId = requestAnimationFrame(loop);
        } else if (!entry.isIntersecting && running) {
          running = false;
          if (rafId) cancelAnimationFrame(rafId);
        }
      });
    }, { threshold: 0.05 });

    build();
    window.addEventListener('resize', build, { passive: true });
    io.observe(canvas);
  }

  /* ---------------------------------------------------------------
     Generic reveal-on-scroll for any .wct-reveal element
     --------------------------------------------------------------- */
  function initReveals() {
    var els = document.querySelectorAll('.wct-reveal');
    if (!els.length) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.15 });
    els.forEach(function (el, i) {
      el.style.transitionDelay = Math.min(i * 60, 300) + 'ms';
      io.observe(el);
    });
  }

  /* ---------------------------------------------------------------
     [ 00d ] Explosion — the stage is pinned by pure CSS (position:
     sticky in .wct-explosion__pin); this scrubs video.currentTime
     directly from scroll position across the section's tall track.
     Scroll events are rAF-aligned in modern browsers, so a passive
     listener with a small seek-delta guard is smooth without any
     ticker/library dependency.
     --------------------------------------------------------------- */
  function initExplosion() {
    var sections = document.querySelectorAll('[data-wct-explosion]');
    if (!sections.length) return;

    sections.forEach(function (section) {
      var video = section.querySelector('video');
      if (!video) return;

      var pendingTime = null;

      function onScroll() {
        if (!video.duration) return;
        var rect = section.getBoundingClientRect();
        var track = section.offsetHeight - window.innerHeight;
        if (track <= 0) return;
        var progress = Math.min(1, Math.max(0, -rect.top / track));
        section.classList.toggle('is-scrubbing', progress > 0.04 && progress < 0.98);
        var target = progress * video.duration;
        // skip micro-seeks; a paused H.264 seek every ~1/30s of media
        // time is plenty smooth and avoids flooding the decoder
        if (pendingTime !== null && Math.abs(target - pendingTime) < 0.033) return;
        pendingTime = target;
        video.currentTime = target;
      }

      video.addEventListener('loadedmetadata', function () {
        video.pause();
        onScroll();
      });
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      video.load();
    });
  }

  /* ---------------------------------------------------------------
     [ 00e ] Orchestration — play the morph loop only while its stage
     is on screen (saves battery/bandwidth on long scroll pages).
     --------------------------------------------------------------- */
  function initOrchestration() {
    var stage = document.querySelector('[data-wct-orchestration] video');
    if (!stage) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          stage.play().catch(function () {});
        } else {
          stage.pause();
        }
      });
    }, { threshold: 0.35 });
    io.observe(stage);
  }

  /* ---------------------------------------------------------------
     Bracket cursor — a small "[ ]" that tracks the pointer and tags
     interactive targets, desktop pointer:fine only. Never blocks
     click/hover on the real element beneath it.
     --------------------------------------------------------------- */
  function initCursor() {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    var scene = document.querySelector('.wct-scene, .spine-main');
    if (!scene) return;

    var cursor = document.createElement('div');
    cursor.className = 'wct-cursor';
    cursor.textContent = '[ ]';
    document.body.appendChild(cursor);

    var raf = null;
    var x = 0, y = 0;
    var overInteractive = false;

    function move(e) {
      x = e.clientX;
      y = e.clientY;
      // interactivity check lives here (not in mouseover/mouseout pairs) so
      // a hovered element being removed from the DOM can't strand the state
      overInteractive = !!e.target.closest('a, button, .wct-btn, [data-wct-interactive]');
      if (raf) return;
      raf = requestAnimationFrame(function () {
        // scale lives in the inline transform too — an inline transform would
        // override any CSS-class scale, so the class only handles opacity
        var scale = overInteractive ? ' scale(1.3)' : '';
        cursor.style.transform = 'translate(' + x + 'px,' + y + 'px) translate(-50%,-50%)' + scale;
        cursor.classList.toggle('is-active', overInteractive);
        document.documentElement.classList.toggle('wct-cursor-active', overInteractive);
        raf = null;
      });
    }

    document.addEventListener('mousemove', move, { passive: true });
  }

  function init() {
    initConstellation();
    initReveals();
    initExplosion();
    initOrchestration();
    initCursor();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
